import React, { useState } from 'react';
import { ArrowTopRightOnSquareIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useCreateBillingPayLink } from '../../../hooks/useBilling';
import { useToast } from '../../../providers/ToastProvider';
import type { DerivedInvoiceStatus } from '../../../services/billing';

const PAYABLE_STATUSES: ReadonlySet<DerivedInvoiceStatus> = new Set(['open', 'partial', 'overdue']);

const PAYMENT_LINK_UNAVAILABLE_MESSAGE =
    'Payment link unavailable — please use the invoice email from Bill.com';
// Bill.com may not issue a link for an already-settled invoice — the backend
// returns the exact same PAYMENT_LINK_UNAVAILABLE error in that case (there's
// no separate error code to distinguish it), so this is a copy-only branch:
// same failure, worded for "view" instead of "pay".
const VIEW_LINK_UNAVAILABLE_MESSAGE =
    'Invoice document unavailable — please use the invoice email from Bill.com';

interface PayButtonProps {
    invoiceId: string;
    derivedStatus: DerivedInvoiceStatus;
    /** 'row' = compact table button; 'drawer' = full-width footer CTA. */
    variant?: 'row' | 'drawer';
    className?: string;
}

/**
 * Pay-link flow: a blank tab is opened synchronously inside the click
 * handler (before the async pay-link request) so browsers don't treat the
 * later `location` assignment as a blocked popup. The interim tab is opened
 * WITHOUT the `noopener` window feature, because that feature makes
 * `window.open` return null and we need the handle to point the tab at the
 * pay link once it arrives. We get the same protection a step later by
 * setting `popup.opener = null` (see below), so the Bill.com page still
 * ends up with no reference back to this tab. If the popup was blocked
 * anyway (`popup` is null/closed), we fall back to a normal
 * `window.open(..., 'noopener')`.
 *
 * Reused for two variants of the same flow: unpaid/payable invoices get the
 * yellow "Pay" CTA, and `paid` invoices get a quiet secondary "View invoice"
 * affordance that hits the same pay-link endpoint (now relaxed server-side
 * to also return a link for settled invoices) purely to open the Bill.com
 * document — no money changes hands, so the styling and copy deliberately
 * don't look like a payment action.
 */
export const PayButton: React.FC<PayButtonProps> = ({
    invoiceId,
    derivedStatus,
    variant = 'row',
    className = ''
}) => {
    const { mutateAsync, isPending } = useCreateBillingPayLink();
    const { showToast } = useToast();
    const [localPending, setLocalPending] = useState(false);
    const pending = isPending || localPending;
    const isPaid = derivedStatus === 'paid';

    const handleOpenLink = async () => {
        if (pending) return;
        setLocalPending(true);
        const popup = window.open('about:blank', '_blank');
        // Disown the interim tab immediately, while it is still same-origin
        // `about:blank` (cross-origin, this property is no longer writable).
        // Nulling `opener` clears it on the browsing context, so it stays null
        // across the later navigation to Bill.com — which means the payment
        // page can never reach back through `window.opener` to redirect this
        // tab. Our own `popup` handle is unaffected, so the
        // `popup.location.href` assignment below still works; only the
        // popup's back-reference to us is severed.
        if (popup) popup.opener = null;
        try {
            const { paymentLink } = await mutateAsync(invoiceId);
            if (popup && !popup.closed) {
                popup.location.href = paymentLink;
            } else {
                window.open(paymentLink, '_blank', 'noopener');
            }
        } catch {
            popup?.close();
            showToast(
                isPaid ? VIEW_LINK_UNAVAILABLE_MESSAGE : PAYMENT_LINK_UNAVAILABLE_MESSAGE,
                'error'
            );
        } finally {
            setLocalPending(false);
        }
    };

    if (isPaid) {
        if (variant === 'drawer') {
            return (
                <button
                    type="button"
                    onClick={() => {
                        void handleOpenLink();
                    }}
                    disabled={pending}
                    className={`w-full inline-flex items-center justify-center gap-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg py-3 text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
                >
                    {pending ? (
                        <span className="h-4 w-4 border-2 border-gray-400/60 border-t-transparent rounded-full animate-spin" />
                    ) : (
                        <>
                            View invoice
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </>
                    )}
                </button>
            );
        }
        // Row variant: keep the settled "Paid" state front and center (green
        // check, unchanged) and add a small quiet link below it — the row's
        // detection zone isn't affected since this cell's <td> already stops
        // click propagation on its own.
        return (
            <div className={`flex flex-col items-center gap-0.5 ${className}`}>
                <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm">
                    <CheckCircleIcon className="h-4 w-4" />
                    Paid
                </span>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        void handleOpenLink();
                    }}
                    disabled={pending}
                    className="inline-flex items-center gap-0.5 whitespace-nowrap text-[11px] font-medium text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                >
                    {pending ? (
                        <span className="h-2.5 w-2.5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                    ) : (
                        <>
                            View invoice
                            <ArrowTopRightOnSquareIcon className="h-2.5 w-2.5" />
                        </>
                    )}
                </button>
            </div>
        );
    }

    if (!PAYABLE_STATUSES.has(derivedStatus)) {
        return <span className={`text-sm text-gray-400 ${className}`}>Not due yet</span>;
    }

    // Yellow/black on every payable row, including overdue — the red "needs
    // attention" signal already lives in the Due date cell and the Status
    // pill, so the button itself stays the same design-system accent
    // (bg-yellow-400/text-gray-900) regardless of urgency.
    const toneClass = 'bg-yellow-400 hover:bg-yellow-500 text-gray-900';

    // Row variant sits next to the StatusBadge pill (rounded-md, same
    // radius) but a notch bigger than a literal pill match — px-2.5/py-1/
    // text-[11px] read as too small next to it in an h-14 row. This is the
    // middle ground between that and the original px-4/py-2/text-sm button.
    // Drawer variant stays a large, full-width footer CTA.
    const sizeClass =
        variant === 'drawer'
            ? 'rounded-lg w-full py-3 text-sm font-semibold'
            : 'rounded-md px-3 py-1.5 text-xs font-semibold';

    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                void handleOpenLink();
            }}
            disabled={pending}
            className={`inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toneClass} ${sizeClass} ${className}`}
        >
            {pending ? (
                <span
                    className={`border-2 border-gray-900/40 border-t-transparent rounded-full animate-spin ${
                        variant === 'drawer' ? 'h-4 w-4' : 'h-3 w-3'
                    }`}
                />
            ) : (
                'Pay'
            )}
        </button>
    );
};
